import { useMemo } from "react";
import { getProductFilters } from "@/api/client";
import { useApiData } from "@/hooks/useApiData";

/**
 * Brand/account/DMA/category dropdown options for the product insight
 * dashboards, mapped once from the product-filters endpoint.
 */
export function useProductFilterOptions() {
  const state = useApiData(getProductFilters);
  const data = state.status === "ready" ? state.data : null;

  const brandOptions = useMemo(
    () =>
      (data?.brands ?? []).map((b) => ({
        value: b.brandId,
        label: b.brandName,
      })),
    [data],
  );
  const accountOptions = useMemo(
    () =>
      (data?.accounts ?? []).map((a) => ({
        value: a.accountId,
        label: a.accountName,
      })),
    [data],
  );
  const dmaOptions = useMemo(
    () => (data?.dmas ?? []).map((dma) => ({ value: dma, label: dma })),
    [data],
  );
  const categoryOptions = useMemo(
    () =>
      (data?.categories ?? []).map((c) => ({
        value: c.categoryId,
        label: c.categoryName,
      })),
    [data],
  );
  const level1CategoryOptions = useMemo(
    () =>
      (data?.level1Categories ?? []).map((c) => ({
        value: c.categoryId,
        label: c.categoryName,
      })),
    [data],
  );
  const storeOptions = useMemo(
    () =>
      (data?.stores ?? []).map((s) => ({
        value: s.storeId,
        label: s.storeName,
      })),
    [data],
  );
  // Prefixed with the owning GFT account so one flat list stays scannable.
  const campaignOptions = useMemo(
    () =>
      (data?.campaigns ?? []).map((c) => ({
        value: c.campaignId,
        label: c.accountName
          ? `${c.accountName} — ${c.campaignName}`
          : c.campaignName,
      })),
    [data],
  );

  return {
    brandOptions,
    accountOptions,
    dmaOptions,
    categoryOptions,
    level1CategoryOptions,
    storeOptions,
    campaignOptions,
    disabled: !data,
  };
}
